import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiTarget, FiCalendar, FiBook, FiTrendingUp, FiExternalLink, FiHeart } = FiIcons;

const ProjectTracker = () => {
  const [projects, setProjects] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    targetWords: '',
    currentWords: 0,
    deadline: '',
    genre: ''
  });

  useEffect(() => {
    const savedProjects = localStorage.getItem('gospelwise-projects');
    if (savedProjects) {
      setProjects(JSON.parse(savedProjects));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('gospelwise-projects', JSON.stringify(projects));
  }, [projects]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingProject) {
      setProjects(projects.map(p => 
        p.id === editingProject.id 
          ? { ...newProject, id: editingProject.id }
          : p
      ));
      setEditingProject(null);
    } else {
      const project = {
        ...newProject,
        id: Date.now(),
        targetWords: parseInt(newProject.targetWords),
        createdAt: new Date().toISOString()
      };
      setProjects([...projects, project]);
    }
    
    setNewProject({
      title: '',
      description: '',
      targetWords: '',
      currentWords: 0,
      deadline: '',
      genre: ''
    });
    setShowAddForm(false);
  };

  const handleEdit = (project) => {
    setNewProject({ ...project, targetWords: project.targetWords.toString() });
    setEditingProject(project);
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    setProjects(projects.filter(p => p.id !== id));
  };

  const handleAddWords = (projectId, words) => {
    setProjects(projects.map(p => 
      p.id === projectId 
        ? { ...p, currentWords: p.currentWords + parseInt(words) }
        : p
    ));
  };

  const resetForm = () => {
    setNewProject({
      title: '',
      description: '',
      targetWords: '',
      currentWords: 0,
      deadline: '',
      genre: ''
    });
    setShowAddForm(false);
    setEditingProject(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg"
      >
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Project Tracker</h1>
            <p className="text-slate-600">
              "For I know the plans I have for you," declares the Lord, "plans to prosper you and not to harm you, 
              to give you hope and a future." - Jeremiah 29:11
            </p>
          </div>
          <div className="flex-shrink-0">
            <SafeIcon icon={FiHeart} className="text-4xl text-red-400" />
          </div>
        </div>
      </motion.div>

      {/* Add Project Button */}
      {!showAddForm && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowAddForm(true)}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
        >
          <SafeIcon icon={FiPlus} className="text-xl" />
          Start New Project
        </motion.button>
      )}

      {/* Add/Edit Project Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-lg"
          >
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              {editingProject ? 'Edit Project' : 'Create New Project'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Title
                  </label>
                  <input
                    type="text"
                    required
                    value={newProject.title}
                    onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter project title"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Genre
                  </label>
                  <select
                    value={newProject.genre}
                    onChange={(e) => setNewProject({...newProject, genre: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select genre</option>
                    <option value="Christian Fiction">Christian Fiction</option>
                    <option value="Biblical Commentary">Biblical Commentary</option>
                    <option value="Devotional">Devotional</option>
                    <option value="Theology">Theology</option>
                    <option value="Biography">Biography</option>
                    <option value="Self-Help">Self-Help</option>
                    <option value="Poetry">Poetry</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Describe your project"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Target Word Count
                  </label>
                  <input
                    type="number"
                    required
                    value={newProject.targetWords}
                    onChange={(e) => setNewProject({...newProject, targetWords: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="50000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Target Deadline
                  </label>
                  <input
                    type="date"
                    value={newProject.deadline}
                    onChange={(e) => setNewProject({...newProject, deadline: e.target.value})}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                >
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-slate-200 text-slate-700 p-3 rounded-lg font-medium hover:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Active Projects */}
      <div className="space-y-4">
        <AnimatePresence>
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddWords={handleAddWords}
            />
          ))}
        </AnimatePresence>
        
        {projects.length === 0 && !showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 text-slate-500"
          >
            <SafeIcon icon={FiBook} className="text-6xl mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-medium mb-2">No projects yet</h3>
            <p>Start your first writing project to begin tracking your progress!</p>
          </motion.div>
        )}
      </div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg"
      >
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <SafeIcon icon={FiHeart} className="text-2xl text-pink-300" />
              <h2 className="text-2xl font-bold">Ready to Share Your Story?</h2>
            </div>
            <p className="text-purple-100 mb-4">
              Every great book starts with a single word. Your story matters, and the world needs to hear it. 
              Take the next step in your writing journey today!
            </p>
          </div>
          <div className="flex-shrink-0">
            <motion.a
              href="https://www.gospelwiseauthor.com/"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full lg:w-auto inline-flex items-center justify-center lg:justify-start gap-2 bg-white text-purple-600 px-6 py-3 rounded-xl font-bold hover:bg-purple-50 transition-all duration-300 shadow-lg"
            >
              Get Publishing Help
              <SafeIcon icon={FiExternalLink} className="text-lg" />
            </motion.a>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center mt-6"
      >
        <p className="text-xs text-slate-400">
          <a 
            href="https://www.gospelwiseauthor.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[#ff8057] hover:text-[#e5714d] transition-colors font-medium"
          >
            Your writing journey starts here
          </a> • Made with ❤️ for Christian writers by CROSS + PEN
        </p>
      </motion.div>
    </div>
  );
};

const ProjectCard = ({ project, onEdit, onDelete, onAddWords }) => {
  const [wordsToAdd, setWordsToAdd] = useState('');
  
  const progress = (project.currentWords / project.targetWords) * 100;
  const daysLeft = project.deadline 
    ? Math.ceil((new Date(project.deadline) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const handleAddWords = (e) => {
    e.preventDefault();
    if (wordsToAdd && parseInt(wordsToAdd) > 0) {
      onAddWords(project.id, parseInt(wordsToAdd));
      setWordsToAdd('');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white/80 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-slate-800 mb-1">{project.title}</h3>
          {project.genre && (
            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-2">
              {project.genre}
            </span>
          )}
          {project.description && (
            <p className="text-sm text-slate-600 mb-3">{project.description}</p>
          )}
        </div>
        
        <div className="flex gap-2 sm:ml-4">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onEdit(project)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiEdit2} className="text-lg" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => onDelete(project.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiTrash2} className="text-lg" />
          </motion.button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-700">Progress</span>
          <span className="text-sm text-slate-600">
            {project.currentWords.toLocaleString()} / {project.targetWords.toLocaleString()} words
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(progress, 100)}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full"
          />
        </div>
        <div className="text-right mt-1">
          <span className="text-xs text-slate-500">{Math.round(progress)}% complete</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <SafeIcon icon={FiTarget} className="text-lg text-blue-600 mx-auto mb-1" />
          <div className="text-sm text-slate-600">Remaining</div>
          <div className="text-sm font-bold text-slate-800">
            {(project.targetWords - project.currentWords).toLocaleString()}
          </div>
        </div>
        
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <SafeIcon icon={FiCalendar} className="text-lg text-green-600 mx-auto mb-1" />
          <div className="text-sm text-slate-600">
            {daysLeft !== null ? (daysLeft > 0 ? 'Days Left' : 'Overdue') : 'No Deadline'}
          </div>
          <div className="text-sm font-bold text-slate-800">
            {daysLeft !== null ? Math.abs(daysLeft) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Add Words */}
      <form onSubmit={handleAddWords} className="flex flex-col sm:flex-row gap-3">
        <input
          type="number"
          min="1"
          placeholder="Words to add"
          value={wordsToAdd}
          onChange={(e) => setWordsToAdd(e.target.value)}
          className="flex-1 p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
        />
        <button
          type="submit"
          className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-700 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300 text-sm"
        >
          Add Words
        </button>
      </form>
    </motion.div>
  );
};

export default ProjectTracker;